
    import { animate, stagger, splitText } from 'animejs';
    import { initNavPill } from './src/lib/navPill.js';

    /* Size the scrolled nav island to its real content so the CTA
       can't hang outside the plate (see src/lib/navPill.js). */
    initNavPill(document.querySelector('.sv-nav'));


    /* Hero headline — per-char blur reveal, mirroring the home page
       hero (.about-hero__headline in src/new-animations.js). Suppress
       the CSS fallback animation, split into chars, prime each at
       opacity 0 / blur(12px), then tween to opacity 1 / blur(0).
       Reduced-motion users get the headline shown without tweens. */
    (function () {
      const headline = document.querySelector('.sv-hero__headline');
      if (!headline) return;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Kill the CSS fallback so it can't double-fire.
      headline.style.animation = 'none';
      headline.style.opacity = '0';
      headline.style.transform = 'none';

      const split = splitText(headline, { chars: { wrap: true, class: 'anime-split-char' }, accessible: true });
      const chars = split.chars;

      for (const c of chars) {
        c.style.opacity = '0';
        c.style.filter  = 'blur(12px)';
      }
      headline.style.opacity = '1';

      if (reduced) {
        for (const c of chars) { c.style.opacity = '1'; c.style.filter = 'none'; }
        return;
      }

      animate(chars, {
        opacity: [0, 1],
        filter: ['blur(12px)', 'blur(0px)'],
        duration: 1500,
        ease: 'outExpo',
        delay: stagger(28, { start: 180 }),
        onComplete: () => { for (const c of chars) c.style.willChange = 'auto'; },
      });
    })();

    /* Lead entrance — mirrors the home page services-section reveal:
       per-char blur+fade on the heading, scale+drop on the chip,
       translateY fade on the supporting copy. One IntersectionObserver
       triggers the lot when the lead is 15% in view. Reduced-motion
       users get the final state without tweens. */
    (function () {
      const heading = document.querySelector('.sv-deliv__heading');
      const chip    = document.querySelector('.sv-deliv__chip');
      const sub     = document.querySelector('.sv-deliv__sub');
      const root    = document.querySelector('.sv-deliv__lead');
      if (!heading || !root) return;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const split   = splitText(heading, { chars: { wrap: true, class: 'anime-split-char' }, accessible: true });
      const chars   = split.chars;

      // Initial state — chars blurred + invisible, chip + sub offset.
      for (const c of chars) {
        c.style.opacity = '0';
        c.style.filter  = 'blur(12px)';
      }
      heading.style.opacity = '1';
      if (chip) { chip.style.opacity = '0'; chip.style.transform = 'translateY(-6px) scale(0.96)'; chip.style.willChange = 'opacity, transform'; }
      if (sub)  { sub.style.opacity  = '0'; sub.style.transform  = 'translateY(10px)'; sub.style.willChange = 'opacity, transform'; }

      if (reduced) {
        for (const c of chars) { c.style.opacity = '1'; c.style.filter = 'none'; }
        if (chip) { chip.style.opacity = '1'; chip.style.transform = 'none'; chip.style.willChange = ''; }
        if (sub)  { sub.style.opacity  = '1'; sub.style.transform  = 'none'; sub.style.willChange  = ''; }
        return;
      }

      const io = new IntersectionObserver((entries, obs) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;

          if (chip) {
            animate(chip, {
              opacity: [0, 1],
              translateY: [-6, 0],
              scale: [0.96, 1],
              duration: 600,
              ease: 'outExpo',
              onComplete: () => { chip.style.willChange = ''; },
            });
          }

          animate(chars, {
            opacity: [0, 1],
            filter: ['blur(12px)', 'blur(0px)'],
            duration: 1500,
            ease: 'outExpo',
            delay: stagger(28, { start: 240 }),
            onComplete: () => {
              for (const c of chars) c.style.willChange = 'auto';
            },
          });

          if (sub) {
            animate(sub, {
              opacity: [0, 1],
              translateY: [10, 0],
              duration: 1100,
              ease: 'outExpo',
              delay: 520,
              onComplete: () => { sub.style.willChange = ''; },
            });
          }

          obs.disconnect();
        }
      }, { threshold: 0.15 });
      io.observe(root);
    })();

    const tabsRoot   = document.querySelector('[data-deliv-tabs]');
    const panelsRoot = document.querySelector('[data-deliv-panels]');
    const selectEl   = document.querySelector('[data-deliv-select]');
    if (tabsRoot && panelsRoot) {
      const tabs    = tabsRoot.querySelectorAll('[data-deliv-tab]');
      const panels  = panelsRoot.querySelectorAll('[data-deliv-panel]');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function activate (idx) {
        tabs.forEach((t) => {
          const active = +t.dataset.delivTab === idx;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panels.forEach((p) => {
          const active = +p.dataset.delivPanel === idx;
          p.classList.toggle('is-active', active);
          if (active && !reduced) {
            animate(p, {
              opacity: [0, 1],
              translateY: [12, 0],
              duration: 460,
              easing: 'easeOutCubic',
            });
          }
        });
        if (selectEl && +selectEl.value !== idx) selectEl.value = String(idx);
      }

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => activate(+tab.dataset.delivTab));
      });

      // Mobile <select> mirrors the tabs.
      if (selectEl) {
        selectEl.addEventListener('change', () => activate(+selectEl.value));
      }
    }
  